/**
 * Document 2 · Phase 10.3c — UserBillingPanel
 *
 * Lives in WorkspaceSettings → Billing tab, ABOVE the admin pricing panel.
 * Shows the current user's subscription state and provides:
 *   - Manage Billing button (opens Stripe Customer Portal in new tab)
 *   - Upgrade CTA (links to /pricing) for trial / lapsed / past_due users
 *
 * Source-aware:
 *   - source=stripe       → tier + period_end + Manage Billing button
 *   - source=trial        → days remaining + prominent Upgrade
 *   - source=override     → "Provided by administrator" — no Stripe options
 *   - source=company      → "Provided by [Company]" — no Stripe options
 *   - source=lapsed       → expired warning + Upgrade
 *
 * No raw Stripe IDs ever shown.
 */
import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  CreditCard, Sparkles, AlertTriangle, CheckCircle2, Clock,
  Building2, Shield, ExternalLink, Loader2, RefreshCw,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';


const TIER_LABEL = {
  starter: 'Starter',
  professional: 'Professional',
  elite: 'Elite',
};

const STATUS_PILL = {
  // status_value: { label, classes }
  active:    { label: 'Active',    classes: 'border-emerald-300 bg-emerald-50 text-emerald-700' },
  trialing:  { label: 'Trial',     classes: 'border-amber-300 bg-amber-50 text-amber-700' },
  trial:     { label: 'Trial',     classes: 'border-amber-300 bg-amber-50 text-amber-700' },
  past_due:  { label: 'Past due',  classes: 'border-red-300 bg-red-50 text-red-700' },
  lapsed:    { label: 'Lapsed',    classes: 'border-zinc-300 bg-zinc-100 text-zinc-700' },
  override:  { label: 'Granted',   classes: 'border-[#B89B5E]/40 bg-[#B89B5E]/10 text-[#B89B5E]' },
  company:   { label: 'Company',   classes: 'border-[#B89B5E]/40 bg-[#B89B5E]/10 text-[#B89B5E]' },
};


function fmtDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return null;
  }
}


function pillFor(ent) {
  if (!ent) return STATUS_PILL.lapsed;
  if (ent.source === 'override') return STATUS_PILL.override;
  if (ent.source === 'company') return STATUS_PILL.company;
  if (ent.source === 'trial' || ent.in_trial) return STATUS_PILL.trial;
  if (ent.is_lapsed && ent.source !== 'stripe') return STATUS_PILL.lapsed;
  if (ent.source === 'stripe') {
    return ent.is_lapsed ? STATUS_PILL.past_due : STATUS_PILL.active;
  }
  return STATUS_PILL.lapsed;
}


function planLabel(ent) {
  if (!ent) return '—';
  if (ent.source === 'trial') return 'Trial · Professional preview';
  if (ent.source === 'lapsed') return 'No active plan';
  if (ent.source === 'override') return `${TIER_LABEL[ent.tier] || 'Granted'} · Admin override`;
  if (ent.source === 'company') return `${TIER_LABEL[ent.tier] || 'Company'} · ${ent.company_name || 'Company plan'}`;
  return TIER_LABEL[ent.tier] || ent.tier || 'Unknown';
}


export default function UserBillingPanel() {
  const api = useApi();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [ent, setEnt] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/billing/entitlement');
      setEnt(data);
    } catch (e) {
      toast.error(e?.message || 'Failed to load billing status');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const openPortal = async () => {
    setOpening(true);
    try {
      const BACKEND = process.env.REACT_APP_BACKEND_URL;
      const res = await axios.post(`${BACKEND}/api/billing/portal`,
        { return_path: '/workspace-settings' },
        { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.portal_url) {
        window.open(res.data.portal_url, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('Could not open portal');
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : (detail?.message || 'Could not open portal');
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  };

  if (loading) {
    return (
      <Card className="rounded-sm border" data-testid="user-billing-loading">
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 text-muted-foreground animate-spin" />
        </CardContent>
      </Card>
    );
  }

  const pill = pillFor(ent);
  const renews = fmtDate(ent?.expires_at);
  const isStripe = ent?.source === 'stripe';
  const isTrial = ent?.source === 'trial';
  const isOverride = ent?.source === 'override' || ent?.source === 'company';
  const isPastDue = isStripe && ent.is_lapsed;
  const isLapsed = ent?.source === 'lapsed';

  return (
    <Card className="rounded-sm border" data-testid="user-billing-panel">
      <CardContent className="p-5 md:p-6 space-y-5">
        {/* Header */}
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="rounded-sm p-2 bg-[#B89B5E]/10 text-[#B89B5E]">
              <CreditCard className="w-5 h-5" />
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Your Plan</div>
              <div className="font-serif text-2xl md:text-3xl leading-none" data-testid="user-billing-plan-name">
                {planLabel(ent)}
              </div>
              <div className="mt-2">
                <Badge variant="outline" className={`rounded-sm text-[10px] ${pill.classes}`} data-testid="user-billing-status-pill">
                  {pill.label}
                </Badge>
              </div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={load} className="rounded-sm" data-testid="user-billing-refresh">
            <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
          </Button>
        </div>

        {/* Status detail bands */}
        {isPastDue && (
          <div
            className="rounded-sm border border-red-300 bg-red-50 text-red-800 px-4 py-3 flex items-start gap-3"
            data-testid="user-billing-past-due-banner"
          >
            <AlertTriangle className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Payment failed — update your payment method</div>
              <div className="text-xs mt-1">
                Your last payment didn't go through. Update your card in the Stripe portal to keep your subscription active.
              </div>
            </div>
          </div>
        )}

        {isLapsed && (
          <div
            className="rounded-sm border border-zinc-300 bg-zinc-50 text-zinc-800 px-4 py-3 flex items-start gap-3"
            data-testid="user-billing-lapsed-banner"
          >
            <Clock className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Your plan has expired</div>
              <div className="text-xs mt-1">
                Resubscribe to unlock AI generation, Vision Decks, and the rest of the platform.
              </div>
            </div>
          </div>
        )}

        {isTrial && (
          <div
            className="rounded-sm border border-amber-300 bg-amber-50 text-amber-800 px-4 py-3 flex items-start gap-3"
            data-testid="user-billing-trial-banner"
          >
            <Sparkles className="w-5 h-5 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <div className="font-semibold text-sm">
                {ent.trial_days_left > 0
                  ? `${ent.trial_days_left} day${ent.trial_days_left === 1 ? '' : 's'} left in your free trial`
                  : 'Trial ends today'}
              </div>
              <div className="text-xs mt-1">
                Upgrade to a paid plan to keep using DH Design after your trial ends. No card needed during trial.
              </div>
            </div>
          </div>
        )}

        {isOverride && (
          <div
            className="rounded-sm border border-[#B89B5E]/40 bg-[#B89B5E]/5 text-foreground px-4 py-3 flex items-start gap-3"
            data-testid="user-billing-override-banner"
          >
            {ent.source === 'company' ? (
              <Building2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#B89B5E]" />
            ) : (
              <Shield className="w-5 h-5 mt-0.5 flex-shrink-0 text-[#B89B5E]" />
            )}
            <div className="flex-1">
              <div className="font-semibold text-sm">
                {ent.source === 'company'
                  ? `Access provided by ${ent.company_name || 'your company'}`
                  : 'Access granted by administrator'}
              </div>
              <div className="text-xs mt-1 text-muted-foreground">
                Billing is handled outside this app. Contact your administrator for plan changes.
              </div>
            </div>
          </div>
        )}

        {isStripe && !isPastDue && (
          <div
            className="rounded-sm border bg-muted/30 px-4 py-3 flex items-start gap-3"
            data-testid="user-billing-stripe-active"
          >
            <CheckCircle2 className="w-5 h-5 mt-0.5 flex-shrink-0 text-emerald-600" />
            <div className="flex-1">
              <div className="font-semibold text-sm">Your subscription is active</div>
              {renews && (
                <div className="text-xs mt-1 text-muted-foreground">
                  Renews on {renews}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Action row */}
        <div className="flex flex-wrap items-center gap-3 pt-2 border-t">
          {(isStripe) && (
            <Button
              size="sm"
              onClick={openPortal}
              disabled={opening}
              className="rounded-sm"
              data-testid="user-billing-manage-btn"
            >
              {opening
                ? (<><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Opening…</>)
                : (<><CreditCard className="w-4 h-4 mr-1.5" /> Manage Billing</>)
              }
            </Button>
          )}

          {(isTrial || isLapsed || isPastDue) && (
            <Link to="/pricing">
              <Button
                size="sm"
                className="rounded-sm"
                style={{ backgroundColor: '#B89B5E', color: 'white' }}
                data-testid="user-billing-upgrade-btn"
              >
                <Sparkles className="w-4 h-4 mr-1.5" />
                {isPastDue ? 'Update payment' : isLapsed ? 'Resubscribe' : 'Upgrade now'}
              </Button>
            </Link>
          )}

          {isOverride && (
            <span className="text-xs text-muted-foreground">No billing actions available.</span>
          )}

          <span className="ml-auto text-[10px] text-muted-foreground flex items-center gap-1">
            <ExternalLink className="w-3 h-3" />
            Stripe handles all payments securely
          </span>
        </div>
      </CardContent>
    </Card>
  );
}
